import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../Components/Header/Header';
import Sidebar from '../../Components/Sidebar/Sidebar';
import { getProviderProfile } from '../../api/providers';
import '../AdventureShared.css';
import './ProviderProfilePage.css';
import SEO from '../../Components/SEO/SEO';
import { generateSlug } from '../../utils/slugHelper';

export default function ProviderProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getProviderProfile(id);
        setProfile(data);
      } catch (err) {
        console.error(err);
        setError("Failed to load provider profile. Make sure the ID is correct.");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [id]);

  if (loading) {
    return (
      <div className="ProviderProfilePage">
        <div className="adventurePageContainer">
          <div className="headerContain"><Header /></div>
          <div className="adventurePageContainer2">
            <div className="sidebarContain"><Sidebar /></div>
            <main className="adv-page-main">
              <div className="adv-state-text" style={{ paddingTop: '80px' }}>Loading provider profile...</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="ProviderProfilePage">
        <div className="adventurePageContainer">
          <div className="headerContain"><Header /></div>
          <div className="adventurePageContainer2">
            <div className="sidebarContain"><Sidebar /></div>
            <main className="adv-page-main">
              <div className="adv-state-error" style={{ paddingTop: '80px', textAlign: 'center' }}>
                <h3>{error || "Provider profile not found!"}</h3>
                <button className="adv-btn-primary" style={{ marginTop: '16px' }} onClick={() => navigate('/facilitate/marketplace')}>
                  Back to Marketplace
                </button>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  // Filter offerings by category tab
  const filteredOfferings = profile.offerings?.filter((off) => {
    if (activeTab === 'all') return true;
    return off.category?.toLowerCase() === activeTab;
  }) || [];

  const formatPrice = (off) => {
    if (off.pricingModel === 'free') return 'Free';
    if (off.pricingModel === 'cod') return 'Cash on Delivery';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: off.currency || 'USD' }).format(off.price || 0);
  };

  const getPricingLabel = (model) => {
    if (model === 'flat') return 'flat fee';
    if (model === 'per_hour') return 'per hour';
    if (model === 'per_day') return 'per day';
    return '';
  };

  return (
    <>
      <SEO 
        title={profile.name}
        description={profile.bio || `View offerings and services provided by ${profile.name} on Amalgamate.`}
        image={profile.avatarUrl}
        slugPath={`/facilitate/providers/${profile.providerId || id}/${generateSlug(profile.name)}`}
      />
      <div className="ProviderProfilePage">
        <div className="adventurePageContainer">
          <div className="headerContain"><Header /></div>
          <div className="adventurePageContainer2">
            <div className="sidebarContain"><Sidebar /></div>
            <main className="adv-page-main">
            {/* Profile Header Grid */}
            <div className="provider-header-card animate-profile-fade-in">
              <div className="provider-avatar-section">
                <img
                  src={profile.avatarUrl || "/profilePic.jpg"}
                  alt={profile.name}
                  className="provider-profile-avatar"
                />
              </div>
              <div className="provider-details-section">
                <h1 className="provider-profile-name">{profile.name}</h1>
                <div className="provider-meta-row">
                  {profile.location && <span className="provider-meta-item">📍 {profile.location}</span>}
                  <span className="provider-meta-item">📅 Member since {new Date(profile.memberSince).getFullYear()}</span>
                </div>
                
                <div className="provider-stats-badges">
                  <div className="provider-stat-badge">
                    <span className="stat-label">Average Rating</span>
                    <span className="stat-value">⭐ {profile.averageRating > 0 ? profile.averageRating.toFixed(1) : 'N/A'}</span>
                  </div>
                  <div className="provider-stat-badge">
                    <span className="stat-label">Reviews</span>
                    <span className="stat-value">💬 {profile.reviewCount} received</span>
                  </div>
                  <div className="provider-stat-badge">
                    <span className="stat-label">Active Listings</span>
                    <span className="stat-value">📦 {profile.offerings?.length || 0} active</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Grid Layout for Offerings Catalog & Reviews */}
            <div className="provider-profile-grid animate-profile-fade-in">
              {/* Left Column: Offerings Catalog & Intro Video */}
              <div className="provider-profile-left">
                {/* Listings Tab Control */}
                <div className="adv-card">
                  <h3 className="adv-card-title">Offerings Catalog</h3>
                  <div className="category-tabs-container">
                    {['all', 'service', 'event', 'product', 'custom'].map((tab) => (
                      <button
                        key={tab}
                        className={`category-tab-btn ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab)}
                      >
                        {tab === 'all' ? 'All Listings' : tab.charAt(0).toUpperCase() + tab.slice(1) + 's'}
                      </button>
                    ))}
                  </div>

                  {filteredOfferings.length === 0 ? (
                    <div className="no-listings-message">
                      No active listings found under this category.
                    </div>
                  ) : (
                    <div className="storefront-offerings-grid">
                      {filteredOfferings.map((off) => (
                        <div
                          key={off.offeringId}
                          className="storefront-offering-card"
                          onClick={() => navigate(`/facilitate/offerings/${off.offeringId}`)}
                        >
                          <div className="offering-card-img-container">
                            <img
                              src={off.coverImageUrl || "https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&w=800&q=80"}
                              alt={off.title}
                              className="offering-card-img"
                            />
                            <span className={`offering-card-badge ${off.category}`}>
                              {off.category}
                            </span>
                          </div>
                          <div className="offering-card-info">
                            <h4 className="offering-card-title">{off.title}</h4>
                            <div className="offering-card-price-row">
                              <span className="offering-card-price">{formatPrice(off)}</span>
                              <span className="offering-card-price-label">{getPricingLabel(off.pricingModel)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {profile.videoUrl && (
                  <div className="adv-card">
                    <h3 className="adv-card-title">Introduction Video</h3>
                    <div className="provider-video-wrapper">
                      <video controls src={profile.videoUrl} className="provider-video-player" />
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Reviews */}
              <div className="provider-profile-right">
                {/* Reviews List */}
                <div className="adv-card">
                  <h3 className="adv-card-title">Reviews ({profile.reviewCount})</h3>
                  
                  {profile.recentReviews?.length === 0 ? (
                    <div className="no-listings-message">
                      No customer reviews submitted yet.
                    </div>
                  ) : (
                    <div className="storefront-reviews-list">
                      {profile.recentReviews.map((rev) => (
                        <div key={rev.reviewId} className="storefront-review-card">
                          <div className="review-card-header">
                            <img
                              src={rev.customerAvatarUrl || "/profilePic.jpg"}
                              alt={rev.customerName}
                              className="review-card-avatar"
                            />
                            <div>
                              <h4 className="review-card-author">{rev.customerName}</h4>
                              <span className="review-card-date">{new Date(rev.createdAt).toLocaleDateString()}</span>
                            </div>
                            <div className="review-card-rating">
                              {Array.from({ length: rev.rating }).map((_, i) => (
                                <span key={i} className="review-star">★</span>
                              ))}
                            </div>
                          </div>
                          
                          <p className="review-card-text">"{rev.comment}"</p>

                          {rev.photoUrls?.length > 0 && (
                            <div className="review-photos-row">
                              {rev.photoUrls.map((url, i) => (
                                <img key={i} src={url} alt="Review attachment" className="review-photo-thumbnail" />
                              ))}
                            </div>
                          )}

                          {rev.providerResponse && (
                            <div className="review-card-reply">
                              <p className="reply-header">Reply from Provider:</p>
                              <p className="reply-text">"{rev.providerResponse}"</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
    </>
  );
}
